import React, { useState, useEffect } from 'react'
import { apiPut, apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import { useForm } from 'react-hook-form'
import useToastContext from 'hooks/useToastContext'
import { useTranslation } from 'react-i18next'
import OInputField from 'components/reusable/OInputField'
import { preventMaxInput } from 'utils/validations'
import ErrorMessage from 'components/ErrorMessage'
import formValidation from '../../utils/formValidation'

const CityEdit = ({ allCity, currentItem, handleEdit }) => {
  const { t } = useTranslation()
  const notification = useToastContext()

  const [countryList, setCountryList] = useState([])
  const [stateList, setStateList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [cat, setCat] = useState(currentItem?.Country?._id || '')
  const [stat, setStat] = useState(currentItem?.State?._id || '')

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      name: currentItem?.name || '',
    }
  })

  const onSubmit = async data => {
    try {
      setIsLoading(true)
      const payload = {
        countryID: cat,
        name: data?.name?.trim(),
        stateID: stat
      }
      const path = `${apiPath.cityUpdate}/${currentItem._id}`
      const result = await apiPut(path, payload)
      if (result.data.success) {
        allCity()
        handleEdit(false)
        notification.success(result?.data.message)
      } else {
        notification.error(result?.data.message)
      }
    } catch (error) {
      console.log('error in city update==>>>>', error)
      notification.error(error.message)
    }
    setIsLoading(false)
  }

  const getCountryList = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {})
      setCountryList(res?.data?.results?.allCountries || [])
    } catch (error) {
      console.log('error fetching countries:', error)
    }
  }

  const getStateList = async () => {
    try {
      const res = await apiGet(apiPath.getStateList, {})
      setStateList(res?.data?.results?.allStates || [])
    } catch (error) {
      console.log('error fetching states:', error)
    }
  }

  useEffect(() => {
    getCountryList()
    getStateList()
  }, [currentItem?.Country?._id])

  const selectCategoryCountry = async (e) => {
    const selectedCountry = e.target.value
    if (selectedCountry) {
      setCat(selectedCountry)
      clearErrors('countryID')
      await getStateList(selectedCountry)
      setStat('') // reset state
    } else {
      setCat('')
      setStateList([])
      setStat('')
      setError('countryID', {
        type: 'validation',
        message: 'Please select country name.'
      })
    }
  }

  const selectCategoryState = (e) => {
    const selectedState = e.target.value
    if (selectedState) {
      setStat(selectedState)
      clearErrors('stateID')
    } else {
      setStat('')
      setError('stateID', {
        type: 'validation',
        message: 'Please select state name.'
      })
    }
  }

  return (
    <>
      <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
        <div className='relative w-auto my-6 mx-auto max-w-3xl'>
          <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
            <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[550px]'>
              <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
                <h3 className='text-xl font-semibold'>{t('EDIT_CITY')}</h3>
                <button
                  className=' ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                  onClick={() => handleEdit(false)}
                  title='Close'
                >
                  <span className=' text-[#B8BBBF] text-4xl ' title='Close'>
                    ×
                  </span>
                </button>
              </div>

              <div className='relative p-6 flex-auto'>
                <div className='grid sm:grid-cols-2'>

                  {/* Country Dropdown */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <select
                        id='countryID'
                        value={cat}
                        onChange={selectCategoryCountry}
                        className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                      >
                        <option value=''>{t('SELECT_COUNTRY')}</option>
                        {countryList.map(item => (
                          <option key={item._id} value={item._id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <label
                        htmlFor='countryID'
                        className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 peer-focus:left-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                      >
                        {t('COUNTRY')}<span className='text-red-500'>*</span>
                      </label>
                      <ErrorMessage message={errors?.countryID?.message} />
                    </div>
                  </div>

                  {/* State Dropdown */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <select
                        id='stateID'
                        value={stat}
                        onChange={selectCategoryState}
                        className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                      >
                        <option value=''>{t('SELECT_STATE')}</option>
                        {stateList.map(item => (
                          <option key={item._id} value={item._id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <label
                        htmlFor='stateID'
                        className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 peer-focus:left-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                      >
                        {t('STATE')}<span className='text-red-500'>*</span>
                      </label>
                      <ErrorMessage message={errors?.stateID?.message} />
                    </div>
                  </div>

                  {/* City Name */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <OInputField
                      name="name"
                      inputLabel={
                        <>
                          {t("CITY")}
                          <span className="text-red-500">*</span>
                        </>
                      }
                      wrapperClassName="relative z-0 w-full group"
                      type="text"
                      autoFocus={true}
                      maxLength={30}
                      onInput={(e) => preventMaxInput(e, 30)}
                      register={register("name", {
                        ...formValidation["cityName"],
                        validate: (value) => {
                          const wordCount = value.trim().split(/\s+/).length;
                          return wordCount <= 4 || "City name cannot exceed 4 words.";
                        },
                      })}
                      id="name"
                      errors={errors}
                      placeholder=""
                    />
                  </div>
                </div>
              </div>

              <div className='justify-center p-6 border-t border-solid flex items-center border-slate-200 rounded-b'>
                <button
                  onClick={() => handleEdit(false)}
                  className='px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 text-black bg-[#E1E1E1] font-normal ease-linear transition-all duration-150'
                  type='button'
                >
                  {t('O_BACK')}
                </button>

                {isLoading ? (
                  <div className='text-white font-normal text-sm px-8 py-2.5 spinner-container bg-LightBlue rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150'>
                    <div className='loading-spinner'></div>
                  </div>
                ) : (
                  <button
                    type='submit'
                    onClick={handleSubmit(onSubmit)}
                    className='font-normal text-sm px-8 py-2.5 bg-LightBlue text-white rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150'
                  >
                    {t('O_SAVE')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='opacity-25 fixed inset-0 z-40 bg-black' />
    </>
  )
}

export default CityEdit
